// Plain-English definitions for the 3 pillars and 4 derived indicators.
// Single source of truth for the Methodology section and the metric drawer.

import type { DerivedMetricKey } from "./types";

export interface MetricDefinition {
  code: string;
  name: string;
  question: string;
  derivation: string;
  pillar: string;
  caveat: string;
}

export const PILLARS = [
  {
    title: "1 · Sleep Opportunity & Quality",
    body: "Did the schedule give people enough opportunity to sleep, and was the sleep they got restorative? Combines sleep duration vs need, consistency of timing, and accumulated sleep debt.",
  },
  {
    title: "2 · Readiness / physiological capacity today",
    body: "How much capacity does the body have to take on load today? Built from recovery, heart-rate variability and resting heart rate relative to each person's own baseline.",
  },
  {
    title: "3 · Load & Timing / job demand",
    body: "What is the job actually asking of people, and when? Combines day strain/load with rota context — shift type, start time, overtime and rest periods.",
  },
];

export const METRIC_DEFINITIONS: Record<DerivedMetricKey, MetricDefinition> = {
  fed: {
    code: "FED",
    name: "Fatigue Exposure Days",
    question: "How often are people starting work already fatigued?",
    derivation:
      "A workday is counted as a fatigue-exposure day when readiness is low and/or sleep debt is high going into the shift. Reported as FED per 1,000 workdays and as the % of workdays in high fatigue exposure, so cohorts of different sizes compare fairly.",
    pillar: "Sleep Opportunity & Quality + Readiness",
    caveat:
      "FED is an operational exposure measure, not a medical diagnosis. It indicates how often work is starting from a fatigued state.",
  },
  lrm: {
    code: "LRM",
    name: "Load–Readiness Misalignment",
    question: "Was the work demand appropriate for their physical state?",
    derivation:
      "The % of days where load/strain was high while readiness was low — i.e. the body was asked to do demanding work without the capacity for it. This is the core operational mismatch signal and powers the recovery-vs-strain hotspot view.",
    pillar: "Load & Timing vs Readiness",
    caveat:
      "LRM highlights where workload and capacity diverge at cohort level; it is not a judgement of any individual.",
  },
  cdi: {
    code: "CDI",
    name: "Circadian Disruption Index",
    question: "Is the schedule itself disrupting sleep and recovery?",
    derivation:
      "A 0–100 score combining sleep-timing variability, low sleep consistency and repeated sleep debt. High CDI usually points to a structural scheduling driver (e.g. sustained night blocks) rather than individual behaviour.",
    pillar: "Sleep Opportunity & Quality",
    caveat:
      "CDI describes the schedule's impact on sleep regularity; use it to evaluate rota design, not to assess people.",
  },
  pdc: {
    code: "PDC",
    name: "Physiological Deviation Cluster",
    question: "Are multiple vitals drifting outside personal baseline together?",
    derivation:
      "Counts when several vitals deviate from a person's own baseline at once — e.g. resting heart rate up, HRV down, respiratory rate up. Reported as the % of a cohort currently showing a cluster.",
    pillar: "Readiness / physiological capacity",
    caveat:
      "PDC is an early 'something is off' signal, NOT a diagnostic tool. It prompts a supportive check-in and rest — never a clinical conclusion.",
  },
};

export const WHY_DERIVED =
  "PEAK4 presents its own operational indicators (FED, LRM, CDI, PDC) rather than exposing raw WHOOP proprietary scores. These derived metrics are built for operational decisions — scheduling, workload and rest — are comparable across cohorts of different sizes, and keep the focus on cohort-level risk and controls rather than individual surveillance. Raw vendor scores are an input, not the product.";

export const NOT_MEDICAL =
  "This dashboard supports operational fatigue risk management. It is not a medical device and does not provide medical diagnosis. Indicators describe fatigue exposure and workload alignment at cohort level, and are intended to prompt support, rest and workload adjustment.";
